<template>
  <article class="about">
    <header>
      <h2 class="h2 article-title">SEO Test Results</h2>
    </header>
    
    <section class="about-text" v-if="loading">
      <div class="content-card">
        <div class="loading-animation">
          <ion-icon name="reload-outline" class="spinner"></ion-icon>
          <p>Loading SEO information...</p>
        </div>
      </div>
    </section>
    
    <template v-else>
      <section class="service">
        <h3 class="h3 service-title">Page Title</h3>
        <div class="content-card">
          <div class="result">{{ title || 'No title found' }}</div>
        </div>
      </section>
      
      <section class="service">
        <h3 class="h3 service-title">Meta Tags</h3>
        <div class="content-card">
          <div class="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name/Property</th>
                  <th>Content</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(meta, index) in metaTags" :key="index">
                  <td>{{ meta.type }}</td>
                  <td>{{ meta.name }}</td>
                  <td>{{ meta.content }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
      
      <section class="service">
        <h3 class="h3 service-title">Required SEO Tags</h3>
        <div class="content-card">
          <div v-if="missingTags.length === 0" class="success-message">
            <div class="status-icon success">
              <ion-icon name="checkmark-circle-outline"></ion-icon>
            </div>
            <p>All required SEO tags are present!</p>
          </div>
          <div v-else class="warning-message">
            <div class="status-icon warning">
              <ion-icon name="alert-circle-outline"></ion-icon>
            </div>
            <p><strong>Warning:</strong> Missing {{ missingTags.length }} required tags:</p>
            <ul class="tag-list">
              <li v-for="(tag, index) in missingTags" :key="index">{{ tag }}</li>
            </ul>
          </div>
        </div>
      </section>
      
      <section class="service">
        <h3 class="h3 service-title">Other Checks</h3>
        
        <div class="content-card">
          <h4 class="h4 service-item-title">Canonical Link</h4>
          <div v-if="canonicalLink" class="success-message">
            <div class="status-icon success">
              <ion-icon name="checkmark-circle-outline"></ion-icon>
            </div>
            <p class="result">{{ canonicalLink }}</p>
          </div>
          <div v-else class="warning-message">
            <div class="status-icon warning">
              <ion-icon name="alert-circle-outline"></ion-icon>
            </div>
            <p>Missing canonical link</p>
          </div>
        </div>
        
        <div class="content-card" style="margin-top: 20px;">
          <h4 class="h4 service-item-title">Structured Data</h4>
          <div v-if="structuredData" class="success-message">
            <div class="status-icon success">
              <ion-icon name="checkmark-circle-outline"></ion-icon>
            </div>
            <pre>{{ JSON.stringify(structuredData, null, 2) }}</pre>
          </div>
          <div v-else class="warning-message">
            <div class="status-icon warning">
              <ion-icon name="alert-circle-outline"></ion-icon>
            </div>
            <p>Missing structured data (JSON-LD)</p>
          </div>
        </div>
      </section>
    </template>
  </article>
</template>

<script>
export default {
  name: 'SeoTester',
  data() {
    return {
      loading: true,
      title: '',
      metaTags: [],
      missingTags: [],
      canonicalLink: '',
      structuredData: null,
      requiredTags: [
        { type: 'name', name: 'description' },
        { type: 'name', name: 'keywords' },
        { type: 'name', name: 'author' },
        { type: 'property', name: 'og:title' },
        { type: 'property', name: 'og:description' },
        { type: 'property', name: 'og:image' },
        { type: 'property', name: 'og:url' },
        { type: 'property', name: 'og:type' },
        { type: 'property', name: 'twitter:title' },
        { type: 'property', name: 'twitter:description' }
      ]
    };
  },
  mounted() {
    setTimeout(() => {
      this.analyzeSEO();
    }, 1000);
  },
  methods: {
    analyzeSEO() {
      // Get page title
      this.title = document.title;
      
      // Get meta tags
      document.querySelectorAll('meta').forEach(meta => {
        let type, name;
        
        if (meta.name) {
          type = 'name';
          name = meta.name;
        } else if (meta.getAttribute('property')) {
          type = 'property';
          name = meta.getAttribute('property');
        } else if (meta.httpEquiv) {
          type = 'http-equiv';
          name = meta.httpEquiv;
        } else {
          type = 'other';
          name = '-';
        }
        
        this.metaTags.push({
          type,
          name,
          content: meta.content || '-'
        });
      });
      
      // Check required tags
      this.missingTags = [];
      this.requiredTags.forEach(tag => {
        let selector;
        if (tag.type === 'name') {
          selector = `meta[name="${tag.name}"]`;
        } else if (tag.type === 'property') {
          selector = `meta[property="${tag.name}"]`;
        }
        
        const metaTag = document.querySelector(selector);
        if (!metaTag || !metaTag.content) {
          this.missingTags.push(`${tag.type}="${tag.name}"`);
        }
      });
      
      // Check canonical link
      const canonical = document.querySelector('link[rel="canonical"]');
      this.canonicalLink = canonical ? canonical.href : '';
      
      // Check structured data
      const structuredDataScript = document.querySelector('script[type="application/ld+json"]');
      if (structuredDataScript) {
        try {
          this.structuredData = JSON.parse(structuredDataScript.textContent);
        } catch (e) {
          console.error('Error parsing structured data:', e);
        }
      }
      
      this.loading = false;
    }
  }
};
</script>

<style scoped>
/* Styling that matches the main site theme */
.content-card {
  position: relative;
  background: var(--border-gradient-onyx);
  padding: 20px;
  border-radius: 14px;
  box-shadow: var(--shadow-2);
  z-index: 1;
  margin-bottom: 20px;
}

.content-card::before {
  content: "";
  position: absolute;
  inset: 1px;
  background: var(--bg-gradient-jet);
  border-radius: inherit;
  z-index: -1;
}

.result {
  font-family: monospace;
  background: rgba(0, 0, 0, 0.2);
  padding: 12px;
  border-radius: 8px;
  word-break: break-word;
  color: var(--light-gray);
}

.loading-animation {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.spinner {
  font-size: 3rem;
  color: var(--orange-yellow-crayola);
  animation: spin 1.5s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.table-responsive {
  overflow-x: auto;
  width: 100%;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin: 15px 0;
}

th, td {
  border: 1px solid var(--jet);
  padding: 10px;
  text-align: left;
}

th {
  background: rgba(255, 215, 108, 0.1);
  color: var(--orange-yellow-crayola);
}

td {
  color: var(--light-gray);
}

pre {
  white-space: pre-wrap;
  background: rgba(0, 0, 0, 0.2);
  padding: 12px;
  border-radius: 8px;
  overflow-x: auto;
  color: var(--light-gray);
  font-size: 14px;
  line-height: 1.5;
}

.success-message, .warning-message {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.status-icon {
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.status-icon.success {
  color: #4caf50;
}

.status-icon.warning {
  color: #ff9800;
}

.tag-list {
  margin: 10px 0 0 30px;
  color: var(--light-gray-70);
}

.tag-list li {
  margin-bottom: 5px;
  font-family: monospace;
}
</style>
